'use client'

import {
    createSystemModule,
    getSystemModule,
    updateSystemModule,
} from '@/lib/api/system-modules/system-modules-api'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdArrowDownward,
    MdArrowUpward,
    MdClose,
    MdSave,
    MdWidgets,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

interface ResourceForm {
    key: string
    label: string
    sortOrder: number
}

interface ModuleForm {
    key: string
    label: string
    sortOrder: number
}

export default function SystemModuleFormPage() {
    const { id } = useParams()
    const isEdit = !!id
    const navigate = useNavigate()

    const [form, setForm] = useState<ModuleForm>({
        key: '',
        label: '',
        sortOrder: 0,
    })
    const [resources, setResources] = useState<ResourceForm[]>([])
    const [originalResourceKeys, setOriginalResourceKeys] = useState<Set<string>>(new Set())
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (isEdit && id) {
            loadModule(parseInt(id, 10))
        }
    }, [id])

    const loadModule = async (moduleId: number) => {
        setLoading(true)
        try {
            const response = await getSystemModule(moduleId)
            if (response.success && response.result) {
                const mod = response.result
                setForm({
                    key: mod.key,
                    label: mod.label,
                    sortOrder: mod.sortOrder,
                })
                const loadedResources = mod.resources.map(r => ({
                    key: r.key,
                    label: r.label,
                    sortOrder: r.sortOrder,
                }))
                setResources(loadedResources)
                setOriginalResourceKeys(new Set(loadedResources.map(r => r.key)))
            } else {
                toast.error('Erro ao carregar módulo')
                navigate('/system-modules')
            }
        } catch {
            toast.error('Erro ao carregar módulo')
            navigate('/system-modules')
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (
        field: keyof ModuleForm,
        value: string | number | string[],
    ) => {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    const handleResourceChange = (
        index: number,
        field: keyof ResourceForm,
        value: string | number | string[],
    ) => {
        setResources(prev => {
            const updated = [...prev]
            updated[index] = { ...updated[index], [field]: value }
            return updated
        })
    }

    const addResource = () => {
        setResources(prev => [
            ...prev,
            { key: '', label: '', sortOrder: prev.length },
        ])
    }

    const removeResource = (index: number) => {
        setResources(prev => prev.filter((_, i) => i !== index))
    }

    const moveResource = (index: number, direction: 'up' | 'down') => {
        setResources(prev => {
            const updated = [...prev]
            const targetIndex = direction === 'up' ? index - 1 : index + 1
            if (targetIndex < 0 || targetIndex >= updated.length) return prev

            const tempOrder = updated[index].sortOrder
            updated[index] = { ...updated[index], sortOrder: updated[targetIndex].sortOrder }
            updated[targetIndex] = { ...updated[targetIndex], sortOrder: tempOrder }

                ;[updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]]
            return updated
        })
    }

    const validate = (): boolean => {
        if (!form.key.trim()) {
            toast.error('A chave do módulo é obrigatória')
            return false
        }

        if (!/^[a-z][a-z0-9_]*$/.test(form.key.trim())) {
            toast.error(
                'A chave deve conter apenas letras minúsculas, números e underscores, começando com letra',
            )
            return false
        }

        if (!form.label.trim()) {
            toast.error('O nome do módulo é obrigatório')
            return false
        }

        if (resources.length === 0) {
            toast.error(
                'Adicione pelo menos um recurso ao módulo',
            )
            return false
        }

        for (let i = 0; i < resources.length; i++) {
            if (!resources[i].key.trim()) {
                toast.error(
                    `A chave do recurso ${i + 1} é obrigatória`,
                )
                return false
            }
            if (
                !/^[a-z][a-z0-9_-]*$/.test(resources[i].key.trim())
            ) {
                toast.error(
                    `A chave do recurso "${resources[i].key}" deve conter apenas letras minúsculas, números, underscores e hífens`,
                )
                return false
            }
            if (!resources[i].label.trim()) {
                toast.error(
                    `O nome do recurso ${i + 1} é obrigatório`,
                )
                return false
            }
        }

        // Verificar chaves duplicadas nos recursos
        const keys = resources.map(r => r.key.trim())
        const duplicates = keys.filter(
            (k, i) => keys.indexOf(k) !== i,
        )
        if (duplicates.length > 0) {
            toast.error(
                `Chave de recurso duplicada: "${duplicates[0]}"`,
            )
            return false
        }

        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) return

        setSaving(true)
        try {
            const payload = {
                key: form.key.trim(),
                label: form.label.trim(),
                sortOrder: form.sortOrder,
                resources: resources.map(r => ({
                    key: r.key.trim(),
                    label: r.label.trim(),
                    sortOrder: r.sortOrder,
                })),
            }

            let response
            if (isEdit && id) {
                const { key: _, ...updatePayload } = payload
                response = await updateSystemModule(
                    parseInt(id, 10),
                    updatePayload,
                )
            } else {
                response = await createSystemModule(payload)
            }

            if (response.success) {
                toast.success(
                    isEdit
                        ? 'Módulo atualizado com sucesso'
                        : 'Módulo criado com sucesso',
                )
                navigate('/system-modules')
            } else {
                toast.error(
                    response.error || 'Erro ao salvar módulo',
                )
            }
        } catch {
            toast.error('Erro ao salvar módulo')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando módulo...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/system-modules')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-lg" />
                </button>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                    <MdWidgets className="text-3xl text-white" />
                </div>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                        {isEdit ? 'Editar Módulo' : 'Novo Módulo'}
                    </h1>
                    <p className="text-sm text-base-content/60 mt-1">
                        {isEdit
                            ? 'Atualize as informações do módulo e seus recursos'
                            : 'Defina o módulo e seus recursos de permissão'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Dados do módulo */}
                <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-bold text-base-content mb-4">
                        Dados do Módulo
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Chave (identificador)
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered font-mono"
                                placeholder="ex: medical"
                                value={form.key}
                                onChange={e =>
                                    handleChange('key', e.target.value)
                                }
                                disabled={isEdit}
                                maxLength={50}
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    Letras minúsculas, números e
                                    underscores
                                </span>
                            </label>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome de exibição
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="ex: Atendimento"
                                value={form.label}
                                onChange={e =>
                                    handleChange(
                                        'label',
                                        e.target.value,
                                    )
                                }
                                maxLength={100}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Ordem de exibição
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered"
                                value={form.sortOrder}
                                onChange={e =>
                                    handleChange(
                                        'sortOrder',
                                        parseInt(e.target.value, 10) ||
                                        0,
                                    )
                                }
                                min={0}
                            />
                        </div>

                        {/*<div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Segmentos
                                </span>
                            </label>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {AVAILABLE_SEGMENTS.map(seg => (
                                    <label
                                        key={seg.value}
                                        className={`
                                            cursor-pointer px-3 py-1.5 rounded-lg border text-sm font-medium
                                            transition-all duration-200
                                            ${form.segments.includes(seg.value)
                                                ? 'bg-secondary text-primary-content border-secondary'
                                                : 'bg-base-200/50 text-base-content/70 border-base-300 hover:border-secondary/50'
                                            }
                                        `}
                                    >
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={form.segments.includes(seg.value)}
                                            onChange={() => toggleModuleSegment(seg.value)}
                                        />
                                        {seg.label}
                                    </label>
                                ))}
                            </div>
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    Vazio = visível para todos os segmentos
                                </span>
                            </label>
                        </div>*/}
                    </div>
                </div>

                {/* Recursos */}
                <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-lg font-bold text-base-content">
                                Recursos
                            </h2>
                            <p className="text-sm text-base-content/60">
                                Defina os recursos (sub-permissões)
                                deste módulo
                            </p>
                        </div>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm gap-1"
                            onClick={addResource}
                        >
                            <MdAdd className="text-lg" />
                            Adicionar
                        </button>
                    </div>

                    {resources.length === 0 ? (
                        <div className="text-center py-8 text-base-content/50 border-2 border-dashed border-base-300 rounded-xl">
                            <p>Nenhum recurso adicionado</p>
                            <p className="text-sm mt-1">
                                Clique em "Adicionar" para criar
                                recursos
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {resources.map((res, index) => (
                                <div
                                    key={index}
                                    className="p-4 bg-base-200/30 rounded-xl border border-base-300/30 space-y-3"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-[50px_1fr_1fr_80px_40px] gap-3">
                                        <div className="flex flex-row md:flex-col items-center justify-center gap-0.5">
                                            {index > 0 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs btn-square"
                                                    onClick={() => moveResource(index, 'up')}
                                                    title="Mover para cima"
                                                >
                                                    <MdArrowUpward className="text-sm" />
                                                </button>
                                            )}
                                            {index < resources.length - 1 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs btn-square"
                                                    onClick={() => moveResource(index, 'down')}
                                                    title="Mover para baixo"
                                                >
                                                    <MdArrowDownward className="text-sm" />
                                                </button>
                                            )}
                                        </div>
                                        <div className="form-control">
                                            <label className="label py-0.5">
                                                <span className="label-text text-xs font-semibold text-base-content/50 uppercase tracking-wider">
                                                    Chave
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered input-sm font-mono"
                                                placeholder="ex: agenda"
                                                value={res.key}
                                                onChange={e =>
                                                    handleResourceChange(
                                                        index,
                                                        'key',
                                                        e.target.value,
                                                    )
                                                }
                                                disabled={isEdit && originalResourceKeys.has(res.key)}
                                                maxLength={50}
                                            />
                                        </div>
                                        <div className="form-control">
                                            <label className="label py-0.5">
                                                <span className="label-text text-xs font-semibold text-base-content/50 uppercase tracking-wider">
                                                    Nome
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered input-sm"
                                                placeholder="ex: Agenda"
                                                value={res.label}
                                                onChange={e =>
                                                    handleResourceChange(
                                                        index,
                                                        'label',
                                                        e.target.value,
                                                    )
                                                }
                                                maxLength={100}
                                            />
                                        </div>
                                        <div className="form-control">
                                            <label className="label py-0.5">
                                                <span className="label-text text-xs font-semibold text-base-content/50 uppercase tracking-wider">
                                                    Ordem
                                                </span>
                                            </label>
                                            <input
                                                type="number"
                                                className="input input-bordered input-sm"
                                                value={res.sortOrder}
                                                onChange={e =>
                                                    handleResourceChange(
                                                        index,
                                                        'sortOrder',
                                                        parseInt(
                                                            e.target.value,
                                                            10,
                                                        ) || 0,
                                                    )
                                                }
                                                min={0}
                                            />
                                        </div>
                                        <div className="flex items-end justify-end md:justify-center pb-0.5">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm btn-square text-error"
                                                onClick={() =>
                                                    removeResource(index)
                                                }
                                                title="Remover recurso"
                                            >
                                                <MdClose className="text-lg" />
                                            </button>
                                        </div>
                                    </div>

                                    {/*<div>
                                        <span className="text-xs font-semibold text-base-content/50 uppercase tracking-wider">
                                            Segmentos do recurso
                                        </span>
                                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                                            {AVAILABLE_SEGMENTS.map(seg => (
                                                <label
                                                    key={seg.value}
                                                    className={`
                                                        cursor-pointer px-2 py-1 rounded-md border text-xs font-medium
                                                        transition-all duration-200
                                                        ${res.segments.includes(seg.value)
                                                            ? 'bg-secondary text-primary-content border-secondary'
                                                            : 'bg-base-100 text-base-content/60 border-base-300 hover:border-secondary/50'
                                                        }
                                                    `}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="hidden"
                                                        checked={res.segments.includes(seg.value)}
                                                        onChange={() => toggleResourceSegment(index, seg.value)}
                                                    />
                                                    {seg.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>*/}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Ações */}
                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/system-modules')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-lg" />
                        )}
                        {isEdit ? 'Salvar Alterações' : 'Criar Módulo'}
                    </button>
                </div>
            </form>
        </div>
    )
}
